import React from 'react';
import { useGetFinanceDashboard } from '@workspace/api-client-react';
import { 
  FileSpreadsheet,
  Download,
  DollarSign,
  Activity,
  Wallet,
  Users,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatZAR } from '@/lib/formatters';

export function FinanceDashboard() {
  const { data: dashboard, isLoading, error } = useGetFinanceDashboard();

  if (isLoading) {
    return <div className="animate-pulse space-y-6"><div className="h-20 bg-gray-200 rounded"></div><div className="grid grid-cols-4 gap-4"><div className="h-32 bg-gray-200 rounded"></div></div></div>;
  }

  if (error || !dashboard) {
    return <div className="text-red-500">Failed to load dashboard.</div>;
  }

  return (
    <div className="space-y-6">
      
      {/* Page Title & Actions */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B132B]">Finance Overview</h1>
          <p className="text-gray-500 mt-1">Review agent commissions and total payouts.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-gray-300 bg-white text-[#0B132B] shadow-sm hover:bg-gray-50">
            <FileSpreadsheet className="w-4 h-4 mr-2 text-gray-500" />
            Payout Report
          </Button>
          <Button className="bg-[#E6007E] hover:bg-[#c4006b] text-white shadow-md font-semibold">
            <Download className="w-4 h-4 mr-2" />
            Generate Bank File
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-5 flex flex-col gap-1">
            <p className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-gray-400" /> Total Once-Off Fees
            </p>
            <p className="text-2xl font-bold text-[#0B132B]">{formatZAR(dashboard.totalOnceOffFeesCents)}</p>
          </CardContent>
        </Card>
        
        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-5 flex flex-col gap-1">
            <p className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-400" /> Total Recurring Comm.
            </p>
            <p className="text-2xl font-bold text-[#0B132B]">{formatZAR(dashboard.totalRecurringCommCents)}</p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-sm bg-[#0B132B] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10 blur-xl"></div>
          <CardContent className="p-5 flex flex-col gap-1 relative z-10">
            <p className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-gray-400" /> Total Payout Value
            </p>
            <p className="text-3xl font-bold text-[#00A859]">{formatZAR(dashboard.totalPayoutValueCents)}</p>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-sm">
          <CardContent className="p-5 flex flex-col gap-1">
            <p className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" /> Agents to Pay
            </p>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-bold text-[#0B132B]">{dashboard.agentsToPay}</p>
            </div>
            {dashboard.payoutsHeld > 0 && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1 font-medium">
                <AlertCircle className="w-3 h-3" /> {dashboard.payoutsHeld} payouts held
              </p>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
